import { useParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardBody, Badge } from '@/components/ui';
import sampleProperties from '../../../data/properties/sample-properties.json';

export default function ParcelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const allProperties = (sampleProperties as any).properties || [];
  const property = allProperties.find((p: any) => p.id === id || p.apn === id);

  const formatCurrency = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
  const formatNumber = (v: number) => new Intl.NumberFormat('en-US').format(v);

  if (!property) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Not Found</h2>
        <p className="text-gray-600 mb-6">
          ID: {id} - Try searching for properties in the search page.
        </p>
        <Link to="/search">
          <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Search Properties
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link to="/search" className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-block">
            ← Back to Search
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">{property.address?.full}</h1>
          <p className="text-gray-600 text-lg">
            {property.address?.city}, {property.address?.state} {property.address?.zip}
          </p>
          <div className="flex gap-2 mt-2">
            <Badge variant="info">{property.propertyType}</Badge>
            <Badge variant={property.condition === 'Excellent' ? 'success' : property.condition === 'Poor' ? 'danger' : 'warning'}>
              {property.condition}
            </Badge>
            <Badge variant={property.tax?.status === 'Current' ? 'success' : 'danger'}>
              Tax: {property.tax?.status}
            </Badge>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Market Value</p>
          <p className="text-4xl font-bold text-green-600">{formatCurrency(property.valuation?.marketValue)}</p>
          <p className="text-sm text-gray-500">APN: {property.apn}</p>
          <p className="text-sm text-gray-500">ID: {property.id}</p>
        </div>
      </div>

      {/* Key Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardBody>
            <p className="text-xs text-blue-600 font-medium">Bedrooms</p>
            <p className="text-2xl font-bold text-blue-900">{property.structure?.bedrooms}</p>
          </CardBody>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardBody>
            <p className="text-xs text-green-600 font-medium">Bathrooms</p>
            <p className="text-2xl font-bold text-green-900">{property.structure?.bathrooms}</p>
          </CardBody>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardBody>
            <p className="text-xs text-purple-600 font-medium">Living Area</p>
            <p className="text-2xl font-bold text-purple-900">{formatNumber(property.parcel?.livingArea)} sq ft</p>
          </CardBody>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardBody>
            <p className="text-xs text-orange-600 font-medium">Lot Size</p>
            <p className="text-2xl font-bold text-orange-900">{property.parcel?.lotSizeAcres} acres</p>
          </CardBody>
        </Card>
        <Card className="bg-pink-50 border-pink-200">
          <CardBody>
            <p className="text-xs text-pink-600 font-medium">Year Built</p>
            <p className="text-2xl font-bold text-pink-900">{property.structure?.yearBuilt}</p>
          </CardBody>
        </Card>
        <Card className="bg-cyan-50 border-cyan-200">
          <CardBody>
            <p className="text-xs text-cyan-600 font-medium">Roof Age</p>
            <p className="text-2xl font-bold text-cyan-900">{property.research?.roofAge} yrs</p>
          </CardBody>
        </Card>
      </div>

      {/* Three Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Property Details */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">🏠 Property Details</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Property Type</span>
                <span className="font-medium">{property.propertyType}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Stories</span>
                <span className="font-medium">{property.structure?.stories}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Total Rooms</span>
                <span className="font-medium">{property.structure?.rooms}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Garage Spaces</span>
                <span className="font-medium">{property.amenities?.garageSpaces || 0}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Basement Area</span>
                <span className="font-medium">{property.parcel?.basementArea || 0} sq ft</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Condition</span>
                <span className="font-medium">{property.condition}</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Construction */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">🔨 Construction</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Foundation</span>
                <span className="font-medium">{property.construction?.foundation}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Roof Type</span>
                <span className="font-medium">{property.construction?.roofType}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Roof Material</span>
                <span className="font-medium">{property.research?.roofMaterial}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Roof Condition</span>
                <span className="font-medium">{property.construction?.roofCondition}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Exterior Walls</span>
                <span className="font-medium">{property.construction?.exteriorWalls}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Year Remodeled</span>
                <span className="font-medium">{property.structure?.yearRemodeled || 'N/A'}</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Systems */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">⚡ Systems</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Heating</span>
                <span className="font-medium">{property.systems?.heating}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Cooling</span>
                <span className="font-medium">{property.systems?.cooling}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Fireplace</span>
                <span className="font-medium">{property.systems?.fireplace ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Sprinkler</span>
                <span className="font-medium">{property.systems?.sprinkler ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Smart Home</span>
                <span className="font-medium">{property.systems?.smartHome ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Solar Panels</span>
                <span className="font-medium">{property.research?.solarPanels ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Amenities & Valuation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Amenities */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">🎯 Amenities & Features</h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <span className={`text-xl ${property.amenities?.pool ? '✅' : '❌'}`}></span>
                <span>Pool</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xl ${property.amenities?.garage ? '✅' : '❌'}`}></span>
                <span>Garage</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xl ${property.amenities?.carport ? '✅' : '❌'}`}></span>
                <span>Carport</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xl">🏔️</span>
                <span>View: {property.amenities?.view || 'None'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xl">🗺️</span>
                <span>FIPS: {property.fips}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xl">📍</span>
                <span>County: {property.county?.split(',')[0]}</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Valuation */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">💰 Valuation & Tax</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Market Value</span>
                <span className="font-bold text-green-600">{formatCurrency(property.valuation?.marketValue)}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Assessed Value</span>
                <span className="font-medium">{formatCurrency(property.valuation?.assessedValue)}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Tax Amount</span>
                <span className="font-medium">{formatCurrency(property.valuation?.taxAmount)}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Rent Estimate</span>
                <span className="font-medium">{formatCurrency(property.valuation?.rentEstimate)}/mo</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Last Sale Price</span>
                <span className="font-medium">{formatCurrency(property.research?.lastSalePrice)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Last Sale Date</span>
                <span className="font-medium">{property.research?.lastSaleDate}</span>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Owner Information */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">👤 Owner Information</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-500">Owner Name</p>
              <p className="text-xl font-semibold">{property.owner?.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Owner Type</p>
              <p className="text-xl font-semibold">{property.owner?.type}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Occupancy Status</p>
              <p className="text-xl font-semibold">{property.owner?.occupancy}</p>
            </div>
          </div>
          {property.tax?.exemptions?.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-gray-500 mb-2">Tax Exemptions</p>
              <div className="flex gap-2">
                {property.tax?.exemptions?.map((ex: string) => (
                  <Badge key={ex} variant="info">{ex}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Coordinates */}
      {property.coordinates && (
        <Card className="bg-gray-50">
          <CardBody>
            <h3 className="text-lg font-semibold mb-2">📍 Coordinates</h3>
            <p>Latitude: {property.coordinates?.lat}</p>
            <p>Longitude: {property.coordinates?.lng}</p>
          </CardBody>
        </Card>
      )}

      {/* Metadata */}
      <Card className="bg-blue-50 border-blue-200">
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Confidence: </span>
              <span className="font-medium">{(property.metadata?.confidence * 100).toFixed(0)}%</span>
            </div>
            <div>
              <span className="text-gray-600">Data Source: </span>
              <span className="font-medium">{property.metadata?.dataSource}</span>
            </div>
            <div>
              <span className="text-gray-600">Last Updated: </span>
              <span className="font-medium">{property.metadata?.lastUpdated}</span>
            </div>
            <div>
              <span className="text-gray-600">FIPS: </span>
              <span className="font-medium">{property.fips}</span>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
